import apiV2 from "api-v2";
import type { PanelMemberWithProgress } from "../types/panel.type";
import type { EvaluationPanelMember } from "../types/panel.type";
import type { DeclineInvitationPayload, ToggleScoringPayload } from "../types/panel.type";
import type {
	MyScoresResponse,
	MemberScoresResponse,
	AverageScoresResponse,
	ComparisonDataResponse,
	PanelScoreSummary,
	SaveScoresPayload,
	SaveScoresResult,
	PanelClarification,
	CreatePanelClarificationPayload,
	UpdatePanelClarificationPayload,
} from "../types/scoring.type";

const unwrap = <T>(responseData: unknown): T => {
	if (responseData && typeof responseData === "object" && "data" in responseData) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

const evalBase = (rfxId: number, evalId: number): string =>
	`/rfx/${rfxId}/evaluation/${evalId}`;

// --- Panel member management (evalId-scoped) ---

export const getPanelMemberStatus = async (
	rfxId: number,
	evalId: number,
	mode: "initial" | "final" = "initial"
): Promise<PanelMemberWithProgress[]> => {
	const params = mode === "final" ? { params: { mode } } : undefined;
	const response = await apiV2.get(`${evalBase(rfxId, evalId)}/panel-members`, params);
	return unwrap<PanelMemberWithProgress[]>(response.data) ?? [];
};

export const acceptInvitation = async (
	rfxId: number,
	evalId: number,
	memberId: number
): Promise<EvaluationPanelMember> => {
	const response = await apiV2.post(
		`${evalBase(rfxId, evalId)}/panel-members/${memberId}/accept`
	);
	return unwrap<EvaluationPanelMember>(response.data);
};

interface GuestAcceptResponse {
	member: EvaluationPanelMember;
	company?: Record<string, unknown>;
	user?: Record<string, unknown>;
}

export const acceptGuestPanelInvitation = async (
	rfxId: number,
	evalId: number,
	memberId: number,
	companyData: Record<string, unknown>
): Promise<GuestAcceptResponse> => {
	const response = await apiV2.post(
		`${evalBase(rfxId, evalId)}/panel-members/${memberId}/accept`,
		{ company_data: companyData }
	);
	return unwrap<GuestAcceptResponse>(response.data);
};

export const declineInvitation = async (
	rfxId: number,
	evalId: number,
	memberId: number,
	payload: DeclineInvitationPayload
): Promise<EvaluationPanelMember> => {
	const response = await apiV2.post(
		`${evalBase(rfxId, evalId)}/panel-members/${memberId}/decline`,
		payload
	);
	return unwrap<EvaluationPanelMember>(response.data);
};

export const reinviteMember = async (
	rfxId: number,
	evalId: number,
	memberId: number
): Promise<EvaluationPanelMember> => {
	const response = await apiV2.put(
		`${evalBase(rfxId, evalId)}/panel-members/${memberId}/reinvite`
	);
	return unwrap<EvaluationPanelMember>(response.data);
};

export const revokeMember = async (
	rfxId: number,
	evalId: number,
	memberId: number
): Promise<EvaluationPanelMember> => {
	const response = await apiV2.put(
		`${evalBase(rfxId, evalId)}/panel-members/${memberId}/revoke`
	);
	return unwrap<EvaluationPanelMember>(response.data);
};

export const sendReminder = async (
	rfxId: number,
	evalId: number,
	memberId: number
): Promise<void> => {
	await apiV2.post(
		`${evalBase(rfxId, evalId)}/panel-members/${memberId}/remind`
	);
};

export const unrevokeMember = async (
	rfxId: number,
	evalId: number,
	memberId: number
): Promise<EvaluationPanelMember> => {
	const response = await apiV2.put(
		`${evalBase(rfxId, evalId)}/panel-members/${memberId}/unrevoke`
	);
	return unwrap<EvaluationPanelMember>(response.data);
};

export const resendMemberInvite = async (
	rfxId: number,
	evalId: number,
	memberId: number
): Promise<void> => {
	await apiV2.post(
		`${evalBase(rfxId, evalId)}/panel-members/${memberId}/resend-invite`
	);
};

export const toggleScoring = async (
	rfxId: number,
	evalId: number,
	payload: ToggleScoringPayload
): Promise<EvaluationPanelMember | null> => {
	const response = await apiV2.put(
		`${evalBase(rfxId, evalId)}/toggle-scoring`,
		payload
	);
	return unwrap<EvaluationPanelMember | null>(response.data);
};

// --- Panel scoring ---

export const saveOrSubmitScores = async (
	rfxId: number,
	evalId: number,
	payload: SaveScoresPayload
): Promise<SaveScoresResult> => {
	const response = await apiV2.post(
		`${evalBase(rfxId, evalId)}/scores`,
		payload
	);
	return unwrap<SaveScoresResult>(response.data);
};

export const getMyScores = async (
	rfxId: number,
	evalId: number
): Promise<MyScoresResponse> => {
	const response = await apiV2.get(`${evalBase(rfxId, evalId)}/scores/my`);
	return unwrap<MyScoresResponse>(response.data);
};

export const getMemberScores = async (
	rfxId: number,
	evalId: number,
	memberId: number,
	mode: "initial" | "final" = "initial"
): Promise<MemberScoresResponse> => {
	const params = mode === "final" ? { params: { mode } } : undefined;
	const response = await apiV2.get(
		`${evalBase(rfxId, evalId)}/scores/member/${memberId}`,
		params
	);
	return unwrap<MemberScoresResponse>(response.data);
};

export const getAverageScores = async (
	rfxId: number,
	evalId: number,
	mode: "initial" | "final" = "initial"
): Promise<AverageScoresResponse> => {
	const params = mode === "final" ? { params: { mode } } : undefined;
	const response = await apiV2.get(`${evalBase(rfxId, evalId)}/scores/averages`, params);
	return unwrap<AverageScoresResponse>(response.data);
};

export const getComparisonData = async (
	rfxId: number,
	evalId: number,
	criteriaId: number,
	inviteId: number,
	mode: "initial" | "final" = "initial"
): Promise<ComparisonDataResponse> => {
	const params: Record<string, unknown> = { invite_id: inviteId };
	if (mode === "final") params.mode = mode;
	const response = await apiV2.get(
		`${evalBase(rfxId, evalId)}/scores/comparison/${criteriaId}`,
		{ params }
	);
	return unwrap<ComparisonDataResponse>(response.data);
};

// --- AI summaries ---

export const triggerAISummary = async (
	rfxId: number,
	evalId: number,
	criteriaId: number
): Promise<void> => {
	await apiV2.post(
		`${evalBase(rfxId, evalId)}/scores/summarise/${criteriaId}`
	);
};

export const getAISummary = async (
	rfxId: number,
	evalId: number,
	criteriaId: number
): Promise<PanelScoreSummary | null> => {
	const response = await apiV2.get(
		`${evalBase(rfxId, evalId)}/scores/summary/${criteriaId}`
	);
	return unwrap<PanelScoreSummary | null>(response.data);
};

// --- Panel clarifications ---

export const createPanelClarification = async (
	rfxId: number,
	evalId: number,
	payload: CreatePanelClarificationPayload
): Promise<PanelClarification> => {
	const response = await apiV2.post(
		`${evalBase(rfxId, evalId)}/clarifications`,
		payload
	);
	return unwrap<PanelClarification>(response.data);
};

export const updatePanelClarification = async (
	rfxId: number,
	evalId: number,
	clarificationId: number,
	payload: UpdatePanelClarificationPayload
): Promise<PanelClarification> => {
	const response = await apiV2.patch(
		`${evalBase(rfxId, evalId)}/clarifications/${clarificationId}`,
		payload
	);
	return unwrap<PanelClarification>(response.data);
};

export const deletePanelClarification = async (
	rfxId: number,
	evalId: number,
	clarificationId: number
): Promise<void> => {
	await apiV2.delete(
		`${evalBase(rfxId, evalId)}/clarifications/${clarificationId}`
	);
};

export const getMyPanelClarifications = async (
	rfxId: number,
	evalId: number
): Promise<PanelClarification[]> => {
	const response = await apiV2.get(
		`${evalBase(rfxId, evalId)}/clarifications/mine`
	);
	return unwrap<PanelClarification[]>(response.data) ?? [];
};

// --- Evaluation progression ---

export const proceedToNextStep = async (
	rfxId: number,
	evalId: number
): Promise<void> => {
	await apiV2.post(`${evalBase(rfxId, evalId)}/proceed`);
};
